import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

export default function useTestBuilder(initialData = null) {
  const [testName, setTestName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [testDescription, setTestDescription] = useState("");
  const [testInstructions, setTestInstructions] = useState("");
  const [testTimeLimit, setTestTimeLimit] = useState("");
  const [categories, setCategories] = useState([]);
  const [gradeRules, setGradeRules] = useState([{ threshold: "", label: "", text: "" }]);
  const [isPrivate, setIsPrivate] = useState(false);

  // NEW: Special modifications
  const [modifications, setModifications] = useState({
    hideMarks: false, // default
  });

  // draft states
  const [draftId, setDraftId] = useState("");
  const [savedDrafts, setSavedDrafts] = useState([]);

  // ================= LOAD INITIAL DATA (EDITOR MODE) =================
  useEffect(() => {
    if (!initialData) return;

    setTestName(initialData.testName || "");
    setTestDescription(initialData.testDescription || "");
    setTestInstructions(initialData.testInstructions || "");
    setTestTimeLimit(initialData.testTimeLimit || 60);
    setQuestions(initialData.questions || []);
    setGradeRules(initialData.gradeRules || [{ threshold: "", label: "", text: "" }]);
    setIsPrivate(initialData.private || false);

    // load modifications if present
    setModifications(initialData.modifications || { hideMarks: false });

    const cats = [];
    initialData.questions?.forEach(q => {
      if (q.category && !cats.includes(q.category)) cats.push(q.category);
    });
    setCategories(cats);
  }, [initialData]);

  // ================= LOAD SAVED DRAFTS FROM LOCALSTORAGE =================
  useEffect(() => {
    const drafts = localStorage.getItem("testDrafts");
    if (drafts) setSavedDrafts(JSON.parse(drafts));
  }, []);

  const addNewCategory = (cat) => {
    setCategories(prev => (prev.includes(cat) ? prev : [...prev, cat]));
  };

  // ================= NESTED MODIFIER =================
  const modifyNestedQuestions = (qs, targetId, modifier) => {
    return qs.map(q => {
      if (q.id === targetId) return modifier(q);
      return {
        ...q,
        options:
          q.options?.map(opt => ({
            ...opt,
            childQuestions: opt.childQuestions?.length
              ? modifyNestedQuestions(opt.childQuestions, targetId, modifier)
              : opt.childQuestions,
          })) || [],
      };
    });
  };

  // ================= QUESTION OPS =================
  const addQuestion = (parentId = null, optionId = null, category = "") => {
    const newQ = {
      id: uuidv4(),
      text: "",
      image: "",
      options: [],
      category,
      parentId,
    };

    setQuestions(prev =>
      parentId && optionId
        ? modifyNestedQuestions(prev, parentId, q => ({
            ...q,
            options: q.options.map(opt =>
              opt.id === optionId
                ? { ...opt, childQuestions: [...(opt.childQuestions || []), newQ] }
                : opt
            ),
          }))
        : [...prev, newQ]
    );
  };

  const updateQuestion = (id, key, value) =>
    setQuestions(prev =>
      modifyNestedQuestions(prev, id, q => {
        if (key !== "category") return { ...q, [key]: value };

        const updateChildren = question => ({
          ...question,
          category: value,
          options: question.options.map(opt => ({
            ...opt,
            childQuestions: (opt.childQuestions || []).map(updateChildren),
          })),
        });

        return updateChildren(q);
      })
    );

  const addOption = (questionId) =>
    setQuestions(prev =>
      modifyNestedQuestions(prev, questionId, q => ({
        ...q,
        options: [
          ...q.options,
          { id: uuidv4(), text: "", weightage: 0, image: "", childQuestions: [] },
        ],
      }))
    );

  const updateOption = (questionId, optionId, key, value) =>
    setQuestions(prev =>
      modifyNestedQuestions(prev, questionId, q => ({
        ...q,
        options: q.options.map(opt =>
          opt.id === optionId ? { ...opt, [key]: value } : opt
        ),
      }))
    );

  const deleteQuestion = (targetId) => {
    const removeNested = qs =>
      qs
        .filter(q => q.id !== targetId)
        .map(q => ({
          ...q,
          options: q.options.map(opt => ({
            ...opt,
            childQuestions: removeNested(opt.childQuestions || []),
          })),
        }));

    setQuestions(prev => removeNested(prev));
  };

  const deleteOption = (questionId, optionId) =>
    setQuestions(prev =>
      modifyNestedQuestions(prev, questionId, q => ({
        ...q,
        options: q.options.filter(opt => opt.id !== optionId),
      }))
    );

  // ================= IMAGE =================
  const handleImageUpload = (e, questionId, optionId = null) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      optionId === null
        ? updateQuestion(questionId, "image", reader.result)
        : updateOption(questionId, optionId, "image", reader.result);
    };
    reader.readAsDataURL(file);
  };

  // ================= GRADE RULES =================
  const updateGradeRule = (i, key, value) =>
    setGradeRules(prev =>
      prev.map((g, index) => (index === i ? { ...g, [key]: value } : g))
    );

  const addGradeRule = () =>
    setGradeRules(prev => [...prev, { threshold: "", label: "", text: "" }]);

  const removeGradeRule = (i) =>
    setGradeRules(prev => prev.filter((_, index) => index !== i));

  // ================= DRAFT SYSTEM =================
  const saveDraft = () => {
    const draftData = {
      id: draftId || uuidv4(),
      testName: testName || "Untitled Test",
      testDescription,
      testInstructions,
      testTimeLimit,
      questions,
      gradeRules,
      private: isPrivate,
      modifications, // save modifications in draft
      lastModified: new Date().toISOString(),
    };

    const existing = JSON.parse(localStorage.getItem("testDrafts") || "[]");

    const updated = draftId
      ? existing.map(d => (d.id === draftId ? draftData : d))
      : [...existing, draftData];

    localStorage.setItem("testDrafts", JSON.stringify(updated));
    setSavedDrafts(updated);
    setDraftId(draftData.id);
  };

  const loadDraft = (draft) => {
    if (!draft) return;

    setTestName(draft.testName || "");
    setTestDescription(draft.testDescription || "");
    setTestInstructions(draft.testInstructions || "");
    setTestTimeLimit(draft.testTimeLimit || 60);
    setQuestions(draft.questions || []);
    setGradeRules(draft.gradeRules || [{ threshold: "", label: "", text: "" }]);
    setIsPrivate(draft.private || false);
    setModifications(draft.modifications || { hideMarks: false });
    setDraftId(draft.id);
  };

  const deleteDraft = (id) => {
    const updated = savedDrafts.filter(d => d.id !== id);
    localStorage.setItem("testDrafts", JSON.stringify(updated));
    setSavedDrafts(updated);
    if (id === draftId) setDraftId("");
  };

  // ============================================================
  // RETURN
  // ============================================================

  return {
    testName, setTestName,
    testDescription, setTestDescription,
    testInstructions, setTestInstructions,
    testTimeLimit, setTestTimeLimit,
    questions, setQuestions,
    categories,
    gradeRules,
    isPrivate, setIsPrivate,

    modifications, setModifications, // <-- expose it

    addQuestion,
    updateQuestion,
    addOption,
    updateOption,
    deleteQuestion,
    deleteOption,
    handleImageUpload,
    addNewCategory,

    updateGradeRule,
    addGradeRule,
    removeGradeRule,

    saveDraft,
    loadDraft,
    deleteDraft,
    savedDrafts,
    draftId,
    setDraftId
  };
}